/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * OnboardingGate — post-phone, post-EULA onboarding sequencer.
 *
 * Mounted INSIDE AppPhoneGate, which only renders its children once a phone is
 * verified (and renders ONLY the phone sheet while unverified). So by the time
 * this gate runs, phone + EULA are already done. It walks the remaining first-
 * run steps in order:
 *
 *   profile → emergency contacts → friend discovery → "all set" → app
 *
 * Full flow across the gate stack:
 *   Welcome → phone entry → OTP   (LoggedOutFlow, before sign-in)
 *     → EULA                      (App.tsx eulaRequired state)
 *     → phone verify confirmation (AppPhoneGate)
 *     → profile → emergency → discovery → all-set → app   (this gate)
 *
 * Step gating:
 *   - Profile is gated on a real RTDB signal (non-placeholder displayName) so
 *     it never re-appears once done — same pattern as useVerifiedPhone.
 *   - Emergency, discovery and the all-set celebration are OPTIONAL/terminal
 *     and write no "done" signal, so they are sequenced with local flow state.
 *     A user who force-quits mid-flow relaunches at the profile step if their
 *     name isn't set, otherwise straight into the app — they are only ever held
 *     by the profile signal, never trapped on a skippable step. Persistent
 *     session: nothing here logs the user out.
 */

import { useState, type ReactNode } from "react";
import { useProfileComplete } from "@/hooks/useProfileComplete";
import ProfileSetupScreen from "@/components/onboarding/ProfileSetupScreen";
import EmergencyContactsScreen from "@/components/onboarding/EmergencyContactsScreen";
import FriendDiscoveryScreen from "@/components/onboarding/FriendDiscoveryScreen";
import AllSetScreen from "@/components/onboarding/AllSetScreen";

interface Props {
  children: ReactNode;
}

// User-facing onboarding steps for the progress bar: phone(1), profile(2),
// emergency(3), discovery(4), all-set(5). Phone is step 1 inside
// PhoneVerificationSheet; the steps below pick up from there.
const TOTAL_STEPS = 5;

type FlowStage = "profile" | "emergency" | "discovery" | "allset" | "done";

export default function OnboardingGate({ children }: Props) {
  const { complete: profileComplete, loading: profileLoading } =
    useProfileComplete();

  // Local flow position for the optional/terminal steps that follow the
  // profile. `null` until the profile state is known.
  const [stage, setStage] = useState<FlowStage | null>(null);

  // Anti-flash: while the first profile snapshot is in flight, render children
  // so a returning, onboarded user never sees a setup screen on cold launch.
  if (profileLoading) return <>{children}</>;

  // A returning, fully-onboarded user (profileComplete and no active flow)
  // falls straight through. A brand-new user enters at `profile`.
  const resolvedStage: FlowStage =
    stage ?? (profileComplete ? "done" : "profile");

  if (resolvedStage === "profile" && !profileComplete) {
    return (
      <ProfileSetupScreen
        step={2}
        totalSteps={TOTAL_STEPS}
        onComplete={() => setStage("emergency")}
      />
    );
  }

  if (resolvedStage === "emergency") {
    return (
      <EmergencyContactsScreen
        step={3}
        totalSteps={TOTAL_STEPS}
        onBack={() => setStage("profile")}
        onContinue={() => setStage("discovery")}
      />
    );
  }

  if (resolvedStage === "discovery") {
    return (
      <FriendDiscoveryScreen
        step={4}
        totalSteps={TOTAL_STEPS}
        onBack={() => setStage("emergency")}
        onContinue={() => setStage("allset")}
      />
    );
  }

  if (resolvedStage === "allset") {
    return (
      <AllSetScreen
        step={5}
        totalSteps={TOTAL_STEPS}
        onEnter={() => setStage("done")}
      />
    );
  }

  // Onboarding complete → the app.
  return <>{children}</>;
}
